import { APP_VERSION } from "../appVersion";
import { fetchWithTimeout } from "../utils/fetchWithTimeout";
import { currentDeviceName, getDeviceId, isInstalledPwa } from "./deviceService";

export type DeviceAcceptanceStatus = "PASS" | "FAIL" | "USER_TO_RUN";

export interface DeviceAcceptanceProbe {
  device_instance_id: string;
  device_label: string;
  device_class: "iphone" | "ipad" | "android-phone" | "android-tablet" | "windows" | "mac" | "other";
  platform: string | null;
  browser_family: "samsung-internet" | "edge" | "firefox" | "chrome" | "safari" | "other";
  browser_major_version: string | null;
  browser_engine: "blink" | "webkit" | "gecko" | "unknown";
  installed_pwa: boolean;
  webgpu_available: boolean;
  wasm_available: boolean;
  cross_origin_isolated: boolean;
  shared_array_buffer_available: boolean;
}

export interface DeviceAcceptanceCheckDefinition { id: string; label: string; description: string; }
export interface DeviceAcceptanceRuntimeSlice { sample_count:number; required_samples:number; p50_ms?:number|null; p95_ms?:number|null; p99_ms?:number|null; }
export interface DeviceAcceptanceEvidence {
  status: DeviceAcceptanceStatus;
  recorded_at?: string|null;
  device_instance_id?: string|null;
  probe?: DeviceAcceptanceProbe|null;
  checks?: Record<string, DeviceAcceptanceStatus>;
  runtime?: { cold?: DeviceAcceptanceRuntimeSlice; warm?: DeviceAcceptanceRuntimeSlice };
  reasons?: string[];
  webgpu_optional_policy_pass?: boolean;
}
export interface DeviceAcceptanceProfile {
  id:string;
  label:string;
  required:boolean;
  conditional?:string;
  device_classes:string[];
  browser_families:string[];
  browser_engines:string[];
  installed_pwa_required:boolean;
  minimum_samples:{cold:number;warm:number};
  p95_limit_ms:number;
  status:DeviceAcceptanceStatus;
  evidence?:DeviceAcceptanceEvidence|null;
  evidence_count:number;
}
export type DeviceResearchEngine = "tesseract" | "neural_ocr_n1" | "domain_ocr_d1" | "hybrid_ocr_h1" | "cnn_geometry" | "live_capture";
export type DeviceResearchField = "materialText" | "materialType" | "materialNumber" | "storageAddress" | "geometry" | "live";
export interface DeviceResearchBenchmark {
  engine: DeviceResearchEngine;
  field: DeviceResearchField;
  sample_count: number;
  accuracy: number;
  runtime_p50_ms: number;
  runtime_p95_ms: number;
  memory_peak_mb?: number|null;
  cold_start_ms?: number|null;
  model_id?: string|null;
  evidence_sha256?: string|null;
}
export interface DeviceResearchRouterRecommendation { device_class:string; field:DeviceResearchField; recommended_engine:DeviceResearchEngine; accuracy:number; runtime_p95_ms:number; sample_count:number; research_only:true; }

export interface DeviceAcceptanceState {
  schema_version:number;
  contract_id:string;
  release:string;
  status:DeviceAcceptanceStatus;
  device_validated:boolean;
  app_version:string;
  active_bundle_id:string|null;
  production_pipeline_sha256:string;
  reference_p95_ms:number;
  runtime_telemetry_sample_count:number;
  runtime_telemetry_missing_sample_count:number;
  required_checks:DeviceAcceptanceCheckDefinition[];
  profiles:DeviceAcceptanceProfile[];
  research_engine_benchmarks?: Array<Record<string,unknown>>;
  field_router_research_recommendations?: DeviceResearchRouterRecommendation[];
  field_router_policy?: {research_only:boolean;production_activation_allowed:boolean;accuracy_primary_runtime_tiebreak:boolean};
  updated_at?:string|null;
}

function matchedVersion(userAgent:string, expressions:RegExp[]):string|null {
  for (const expression of expressions) {
    const match=expression.exec(userAgent);
    if(match?.[1]) return match[1];
  }
  return null;
}

function browserIdentity(userAgent:string): Pick<DeviceAcceptanceProbe,"browser_family"|"browser_major_version"|"browser_engine"> {
  const isIos=/iPhone|iPad|iPod/i.test(userAgent);
  if(/SamsungBrowser\//i.test(userAgent)){const v=matchedVersion(userAgent,[/SamsungBrowser\/([\d.]+)/i]);return{browser_family:"samsung-internet",browser_major_version:v?.split(".")[0]??null,browser_engine:"blink"};}
  if(/Edg(?:A|iOS)?\//i.test(userAgent)){const v=matchedVersion(userAgent,[/EdgA\/([\d.]+)/i,/EdgiOS\/([\d.]+)/i,/Edg\/([\d.]+)/i]);return{browser_family:"edge",browser_major_version:v?.split(".")[0]??null,browser_engine:isIos?"webkit":"blink"};}
  if(/FxiOS\//i.test(userAgent)){const v=matchedVersion(userAgent,[/FxiOS\/([\d.]+)/i]);return{browser_family:"firefox",browser_major_version:v?.split(".")[0]??null,browser_engine:"webkit"};}
  if(/Firefox\//i.test(userAgent)){const v=matchedVersion(userAgent,[/Firefox\/([\d.]+)/i]);return{browser_family:"firefox",browser_major_version:v?.split(".")[0]??null,browser_engine:"gecko"};}
  if(/CriOS\//i.test(userAgent)){const v=matchedVersion(userAgent,[/CriOS\/([\d.]+)/i]);return{browser_family:"chrome",browser_major_version:v?.split(".")[0]??null,browser_engine:"webkit"};}
  if(/Chrome\//i.test(userAgent)||/Chromium\//i.test(userAgent)){const v=matchedVersion(userAgent,[/Chrome\/([\d.]+)/i,/Chromium\/([\d.]+)/i]);return{browser_family:"chrome",browser_major_version:v?.split(".")[0]??null,browser_engine:"blink"};}
  if(/Safari\//i.test(userAgent)){const v=matchedVersion(userAgent,[/Version\/([\d.]+)/i]);return{browser_family:"safari",browser_major_version:v?.split(".")[0]??null,browser_engine:"webkit"};}
  return{browser_family:"other",browser_major_version:null,browser_engine:"unknown"};
}

function deviceClass(userAgent:string):DeviceAcceptanceProbe["device_class"] {
  if(/iPhone|iPod/i.test(userAgent))return"iphone";
  if(/iPad/i.test(userAgent))return"ipad";
  if(/Android/i.test(userAgent))return/Mobile/i.test(userAgent)?"android-phone":"android-tablet";
  if(/Windows/i.test(userAgent))return"windows";
  if(/Macintosh|Mac OS X/i.test(userAgent))return"mac";
  return"other";
}

export function captureDeviceAcceptanceProbe():DeviceAcceptanceProbe {
  const userAgent=navigator.userAgent||"";
  const runtimeNavigator=navigator as Navigator & {gpu?:unknown;userAgentData?:{platform?:string}};
  return {
    device_instance_id:getDeviceId(),
    device_label:currentDeviceName(),
    device_class:deviceClass(userAgent),
    platform:runtimeNavigator.userAgentData?.platform||navigator.platform||null,
    ...browserIdentity(userAgent),
    installed_pwa:isInstalledPwa(),
    webgpu_available:Boolean(runtimeNavigator.gpu),
    wasm_available:typeof WebAssembly!=="undefined",
    cross_origin_isolated:globalThis.crossOriginIsolated===true,
    shared_array_buffer_available:typeof SharedArrayBuffer!=="undefined",
  };
}

export function matchingDeviceAcceptanceProfile(state:DeviceAcceptanceState,probe:DeviceAcceptanceProbe):DeviceAcceptanceProfile|null {
  return state.profiles.find((profile)=>profile.device_classes.includes(probe.device_class)&&profile.browser_families.includes(probe.browser_family)&&profile.browser_engines.includes(probe.browser_engine))??null;
}

export async function fetchDeviceAcceptanceState(signal?:AbortSignal):Promise<DeviceAcceptanceState>{
  const response=await fetchWithTimeout("/api/admin/vision-optimizer/device-acceptance",{credentials:"include",cache:"no-store",signal,headers:{Accept:"application/json"}},15_000);
  if(!response.ok) throw new Error(`Geräteabnahme konnte nicht geladen werden (${response.status}).`);
  return response.json() as Promise<DeviceAcceptanceState>;
}

export async function saveCurrentDeviceAcceptanceEvidence(profileId:string,checks:Record<string,DeviceAcceptanceStatus>,note=""):Promise<DeviceAcceptanceState>{
  const response=await fetchWithTimeout("/api/admin/vision-optimizer/device-acceptance/evidence",{
    method:"POST",credentials:"include",headers:{"Content-Type":"application/json",Accept:"application/json"},
    body:JSON.stringify({profile_id:profileId,probe:captureDeviceAcceptanceProbe(),checks,note,app_version:APP_VERSION}),
  },15_000);
  if(!response.ok){const payload=await response.json().catch(()=>({})) as {detail?:string};throw new Error(payload.detail||`Geräteabnahme konnte nicht gespeichert werden (${response.status}).`);}
  return response.json() as Promise<DeviceAcceptanceState>;
}
export async function saveDeviceResearchBenchmarks(profileId:string,checks:Record<string,DeviceAcceptanceStatus>,benchmarks:DeviceResearchBenchmark[],note="research benchmark"):Promise<DeviceAcceptanceState>{
  const response=await fetchWithTimeout("/api/admin/vision-optimizer/device-acceptance/evidence",{
    method:"POST",credentials:"include",headers:{"Content-Type":"application/json",Accept:"application/json"},
    body:JSON.stringify({profile_id:profileId,probe:captureDeviceAcceptanceProbe(),checks,note,app_version:APP_VERSION,research_benchmarks:benchmarks}),
  },15_000);
  if(!response.ok){const payload=await response.json().catch(()=>({})) as {detail?:string};throw new Error(payload.detail||`Research-Gerätebenchmark konnte nicht gespeichert werden (${response.status}).`);}
  return response.json() as Promise<DeviceAcceptanceState>;
}
